import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { toast } from 'react-hot-toast';
import CityModal from './CityModal';

const CitiesSection = ({ cities, setCities, globalSearch, refreshTrigger }) => {
  const [filteredCities, setFilteredCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCity, setSelectedCity] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [expandedCities, setExpandedCities] = useState(new Set());

  // Get unique countries for filter
  const countries = [...new Set(cities?.map(city => city?.country))];

  // Filter and search cities
  useEffect(() => {
    let filtered = [...cities];

    // Apply search filter
    if (globalSearch?.trim()) {
      filtered = filtered?.filter(city => 
        city?.name?.toLowerCase()?.includes(globalSearch?.toLowerCase()) ||
        city?.country?.toLowerCase()?.includes(globalSearch?.toLowerCase()) ||
        city?.state?.toLowerCase()?.includes(globalSearch?.toLowerCase()) ||
        city?.areas?.some(area => 
          area?.name?.toLowerCase()?.includes(globalSearch?.toLowerCase()) ||
          area?.pinCodes?.some(pin => pin?.includes(globalSearch))
        )
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered?.filter(city => city?.status === filterStatus);
    }

    // Apply country filter
    if (filterCountry !== 'all') {
      filtered = filtered?.filter(city => city?.country === filterCountry);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      if (sortBy === 'name') return a?.name?.localeCompare(b?.name);
      if (sortBy === 'country') return a?.country?.localeCompare(b?.country);
      if (sortBy === 'areas') return (b?.areas?.length || 0) - (a?.areas?.length || 0);
      if (sortBy === 'date') return new Date(b?.updatedAt) - new Date(a?.updatedAt);
      return 0;
    });

    setFilteredCities(filtered);
    setCurrentPage(1);
  }, [cities, globalSearch, filterStatus, filterCountry, sortBy, refreshTrigger]);

  const handleCreateCity = () => {
    setSelectedCity(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditCity = (city) => {
    setSelectedCity(city);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSaveCity = async (cityData, mode) => {
    if (mode === 'create') {
      const newCity = {
        id: Date.now(),
        ...cityData,
        createdAt: new Date()?.toISOString()?.split('T')?.[0],
        updatedAt: new Date()?.toISOString()?.split('T')?.[0]
      };
      setCities(prev => [...prev, newCity]);
    } else if (mode === 'edit') {
      setCities(prev => prev?.map(city => 
        city?.id === selectedCity?.id 
          ? { 
              ...city, 
              ...cityData,
              updatedAt: new Date()?.toISOString()?.split('T')?.[0]
            }
          : city
      ));
    }
  };

  const handleManageAreas = (city) => {
    setSelectedCity(city);
    setModalMode('area');
    setShowModal(true);
  };

  const handleDeleteCity = (cityId) => {
    if (window?.confirm('Are you sure you want to delete this city? This will also remove all associated areas.')) {
      setCities(prev => prev?.filter(city => city?.id !== cityId));
      toast?.success('City deleted successfully');
    }
  };

  const handleToggleStatus = (cityId) => {
    setCities(prev => prev?.map(city => 
      city?.id === cityId 
        ? { ...city, status: city?.status === 'active' ? 'inactive' : 'active' }
        : city
    ));
    toast?.success('City status updated');
  };

  const handleToggleExpanded = (cityId) => {
    const newExpanded = new Set(expandedCities);
    if (newExpanded?.has(cityId)) {
      newExpanded?.delete(cityId);
    } else {
      newExpanded?.add(cityId);
    }
    setExpandedCities(newExpanded);
  };

  // Pagination
  const totalPages = Math.ceil(filteredCities?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCities = filteredCities?.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="flex-1 overflow-hidden bg-background">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 bg-card border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Cities & Areas</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage geographic coverage with cities, areas, and delivery zones
              </p>
            </div>
            <button
              onClick={handleCreateCity}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
            >
              <Icon name="Plus" size={16} />
              <span>Add City</span>
            </button>
          </div>

          {/* Filters */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              
              <select
                value={filterCountry}
                onChange={(e) => setFilterCountry(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Countries</option>
                {countries?.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="name">Sort by Name</option>
                <option value="country">Sort by Country</option>
                <option value="areas">Sort by Areas Count</option>
                <option value="date">Sort by Date</option>
              </select>
            </div>
            
            <div className="text-sm text-muted-foreground">
              {filteredCities?.length} cities found
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {paginatedCities?.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
                <Icon name="MapPin" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No cities found</h3>
              <p className="text-muted-foreground mb-4">
                {globalSearch ? 'No cities match your search criteria.' : 'Get started by adding your first city.'}
              </p>
              {!globalSearch && (
                <button
                  onClick={handleCreateCity}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Add City
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {paginatedCities?.map((city) => (
                <div key={city?.id} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  {/* City Header */}
                  <div className="p-4 border-b border-border">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-foreground">{city?.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            city?.status === 'active' ?'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                          }`}>
                            {city?.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {city?.state}, {city?.country}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center space-x-1">
                            <Icon name="MapPin" size={12} />
                            <span>{city?.areas?.length || 0} areas</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Icon name="Navigation" size={12} />
                            <span>{city?.deliveryRadius}km radius</span>
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleToggleExpanded(city?.id)}
                          className="p-2 hover:bg-muted rounded transition-colors"
                          title="View Areas"
                        >
                          <Icon 
                            name={expandedCities?.has(city?.id) ? "ChevronUp" : "ChevronDown"} 
                            size={16} 
                            className="text-muted-foreground" 
                          />
                        </button>
                        <button
                          onClick={() => handleManageAreas(city)}
                          className="p-2 hover:bg-muted rounded transition-colors"
                          title="Manage Areas"
                        >
                          <Icon name="Settings" size={16} className="text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => handleEditCity(city)}
                          className="p-2 hover:bg-muted rounded transition-colors"
                          title="Edit City"
                        >
                          <Icon name="Edit2" size={16} className="text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(city?.id)}
                          className="p-2 hover:bg-muted rounded transition-colors"
                          title={`${city?.status === 'active' ? 'Deactivate' : 'Activate'} City`}
                        >
                          <Icon 
                            name={city?.status === 'active' ? "EyeOff" : "Eye"} 
                            size={16} 
                            className="text-muted-foreground" 
                          />
                        </button>
                        <button
                          onClick={() => handleDeleteCity(city?.id)}
                          className="p-2 hover:bg-destructive/10 rounded transition-colors"
                          title="Delete City"
                        >
                          <Icon name="Trash2" size={16} className="text-destructive" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Areas List */}
                  {expandedCities?.has(city?.id) && (
                    <div className="p-4 bg-muted/30 max-h-48 overflow-y-auto">
                      {city?.areas?.length > 0 ? (
                        <div className="space-y-3">
                          {city?.areas?.map((area) => (
                            <div key={area?.id} className="bg-card p-3 rounded border">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="text-sm font-medium text-foreground">{area?.name}</h4>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {area?.pinCodes?.slice(0, 3)?.map((pin, index) => (
                                      <span key={index} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                                        {pin}
                                      </span>
                                    ))}
                                    {area?.pinCodes?.length > 3 && (
                                      <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs">
                                        +{area?.pinCodes?.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button
                                    className="p-1 hover:bg-muted rounded transition-colors"
                                    title="Edit Area"
                                  >
                                    <Icon name="Edit2" size={12} className="text-muted-foreground" />
                                  </button>
                                  <button
                                    className="p-1 hover:bg-destructive/10 rounded transition-colors"
                                    title="Remove Area"
                                  >
                                    <Icon name="X" size={12} className="text-destructive" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-sm text-muted-foreground">
                          No areas configured. 
                          <button 
                            onClick={() => handleManageAreas(city)}
                            className="ml-1 text-primary hover:underline"
                          >
                            Add areas
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredCities?.length)} of {filteredCities?.length}
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <span className="px-4 py-2 text-sm">
                  {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <CityModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        mode={modalMode}
        city={selectedCity}
        onSave={handleSaveCity}
        cities={cities}
      />
    </div>
  );
};

export default CitiesSection;